import { useEffect, useRef, useState } from 'react';
import { Dialog } from './dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { CourseFeedbackPromptDto } from '../../general/dtos';
import { validEmailAddress } from '../../general/email';
import { useApi } from '../../providers/api-provider';
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';

export function CourseCompletionEmailDialog({ course, defaultEmail, onDone, embedded = false, onSent, onBusyChange }: {
    embedded?: boolean; onSent?: () => void; onBusyChange?: (busy: boolean) => void;
    course: CourseFeedbackPromptDto; defaultEmail: string; onDone: () => void;
}) {
    const api = useApi();
    const parent = useFullscreenDialogParent();
    const [email, setEmail] = useState(defaultEmail);
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const pending = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const send = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!api || pending.current || !validEmailAddress(email)) return;
        pending.current = true; setBusy(true); onBusyChange?.(true); setError('');
        try {
            await api.sendCourseCompletionEmail(course.course_key, email.trim());
            if (mounted.current) { setSent(true); onSent?.(); }
        } catch (failure: any) {
            if (mounted.current) setError(failure?.response?.data?.message || failure?.message || 'Could not send your confirmation. Please try again.');
        } finally { pending.current = false; if (mounted.current) { setBusy(false); onBusyChange?.(false); } }
    };
    const content = <>
        {sent ? <>
            <p role="status">Your confirmation for <strong>{course.course_name}</strong> has been sent to {email.trim()}.</p>
            {!embedded && <div className="flex justify-content-end"><Button label="Close" onClick={onDone} /></div>}
        </> : <form onSubmit={send} className="flex flex-column gap-3">
            <p className="m-0">Would you like an email confirming completion of <strong>{course.course_name}</strong>?</p>
            <p className="m-0 text-color-secondary">{course.certificate_enabled === false
                ? 'This course does not include a certificate. The email will confirm your course completion.'
                : 'The email includes a public certificate link only if you have confirmed the name on your certificate.'}</p>
            <label htmlFor="completion-email">Email address</label>
            <InputText id="completion-email" type="email" required maxLength={254} autoComplete="email" value={email}
                disabled={busy} onChange={event => { setEmail(event.target.value); setError(''); }} className="w-full" />
            {error && <div role="alert" className="p-3 border-round surface-100 text-red-500">{error}</div>}
            <div className="flex justify-content-end gap-2">
                {!embedded && <Button type="button" label="Skip" text disabled={busy} onClick={onDone} />}
                <Button type="submit" label="Send confirmation" icon="pi pi-send" loading={busy} disabled={busy || !api || !validEmailAddress(email)} />
            </div>
        </form>}
    </>;
    if (embedded) return content;
    return <Dialog header="Course completion confirmation" visible modal appendTo={parent} baseZIndex={11000}
        style={{ width: '32rem', maxWidth: '95vw' }} draggable={false}
        closable={!busy} closeOnEscape={!busy} onHide={() => { if (!busy) onDone(); }}>
        {content}
    </Dialog>;
}
